import React from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * The FILLED readiness gauge on the results page — the payoff that resolves the
 * homepage hero's empty "?" into the visitor's real number, in the same teal
 * instrument. The white arc DRAWS from empty to their score on mount (the one
 * moment motion earns its keep); prefers-reduced-motion renders the final state
 * instantly. Mirrors the .pen "Readiness Hero" ring.
 */
export const FilledReadinessGauge: React.FC<{ score: number; size?: number }> = ({ score, size = 140 }) => {
  const reduce = useReducedMotion();
  const stroke = Math.round((size * 13) / 140);
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, Math.round(score)));
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth={stroke} />
        <motion.circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={reduce ? false : { strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: 'easeOut' }}
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display font-bold text-white leading-none" style={{ fontSize: `${size / 56}rem` }}>{pct}%</span>
        <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-white/60">On these six</span>
      </div>
    </div>
  );
};

/** The report's two-column breakdown; shared by /try's results screen and the handoff sheets. */
export const ResultsBreakdown: React.FC<{ weak: string[]; strong: string[]; noStrongText?: string }> = ({
  weak,
  strong,
  noStrongText = 'Six questions is not enough to call an area strong yet.',
}) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-bold text-amber-800 mb-2">🎯 Focus here first</p>
      {weak.length ? <ul className="text-sm text-neutral-700 space-y-1">{weak.map((c) => <li key={c}>• {c}</li>)}</ul> : <p className="text-sm text-neutral-700">Nothing missed on these six. Six questions is a short sample, not a finished picture.</p>}
    </div>
    <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
      <p className="text-sm font-bold text-emerald-800 mb-2">✓ Already strong</p>
      {strong.length ? <ul className="text-sm text-neutral-700 space-y-1">{strong.map((c) => <li key={c}>• {c}</li>)}</ul> : <p className="text-sm text-neutral-700">{noStrongText}</p>}
    </div>
  </div>
);
